import { round } from 'lodash';
import getNiceStepCeil from '../../../utils/get-nice-step-ceil';
import { BridgeChartRow } from '../types';
import {
  DEFAULT_Y_AXIS_STEP,
  POSITIVE_Y_AXIS_STEP_COUNT,
  VALUE_BAND_BELOW_NEGATIVE_RATIO,
} from './constants';
import { getMaxColumnTop, getMiddleBridgeExtents } from './bridge-extents';

const ensureStepClearsColumnTops = (
  referenceLineValue: number,
  step: number,
  maxColumnTop: number,
) => {
  let adjustedStep = step;

  if (
    referenceLineValue + POSITIVE_Y_AXIS_STEP_COUNT * adjustedStep <=
    maxColumnTop
  ) {
    adjustedStep =
      getNiceStepCeil(
        (maxColumnTop - referenceLineValue) / POSITIVE_Y_AXIS_STEP_COUNT,
      ) || adjustedStep;
  }

  if (
    referenceLineValue + POSITIVE_Y_AXIS_STEP_COUNT * adjustedStep <=
    maxColumnTop
  ) {
    adjustedStep = round(adjustedStep + 0.01, 2);
  }

  return adjustedStep;
};

const buildStandardBridgeYAxisLayout = ({
  referenceLineValue,
  step: initialStep,
  maxColumnTop,
  usesBridgeFocusedAxis,
  dataMin,
}: {
  referenceLineValue: number;
  step: number;
  maxColumnTop: number;
  usesBridgeFocusedAxis: boolean;
  dataMin?: number;
}) => {
  const step = ensureStepClearsColumnTops(
    referenceLineValue,
    initialStep,
    maxColumnTop,
  );
  const displayedNegativeTick = round(referenceLineValue - step, 2);
  const valueBandStep = round(step * VALUE_BAND_BELOW_NEGATIVE_RATIO, 2);
  const valueBandFloor = round(displayedNegativeTick - valueBandStep, 2);
  const positiveTicks = Array.from(
    { length: POSITIVE_Y_AXIS_STEP_COUNT },
    (_, index) => round(referenceLineValue + (index + 1) * step, 2),
  );
  const topGridLine =
    positiveTicks[positiveTicks.length - 1] ?? referenceLineValue;
  const ticks = [
    valueBandFloor,
    displayedNegativeTick,
    referenceLineValue,
    ...positiveTicks,
  ].filter((tick, index, array) => array.indexOf(tick) === index);
  const domainMin =
    dataMin !== undefined ? Math.min(valueBandFloor, dataMin) : valueBandFloor;
  const domainMax = round(Math.max(topGridLine, maxColumnTop + 0.01), 2);
  const domain: [number, number] = [domainMin, domainMax];

  return {
    domain,
    ticks,
    horizontalGridValues: ticks.filter((tick) => tick !== referenceLineValue),
    hiddenTickValues: [valueBandFloor],
    referenceLineValue,
    usesBridgeFocusedAxis,
    step,
    valueBandStep,
    valueBandTop: displayedNegativeTick,
    valueBandFloor,
    yAxisLabelOffset: 0,
  };
};

export const buildAbsoluteBridgeYAxis = (chartRows: Array<BridgeChartRow>) => {
  const middleRows = chartRows.filter((row) => !row.isTotal);
  const totalRows = chartRows.filter((row) => row.isTotal);
  const middleStackTops = middleRows.map((row) =>
    round(row.base + row.value, 2),
  );
  const middleStackBottoms = middleRows.map((row) => row.base);
  const totalStackTops = totalRows.map((row) => round(row.base + row.value, 2));

  const maxPositive = Math.max(0, ...middleStackTops, ...totalStackTops);
  const minNegative = Math.min(0, ...middleStackBottoms);

  const { maxMiddleDelta } = getMiddleBridgeExtents(middleRows);

  const positiveExtent = Math.max(maxPositive, maxMiddleDelta);
  const step =
    positiveExtent > 0
      ? getNiceStepCeil(positiveExtent / POSITIVE_Y_AXIS_STEP_COUNT)
      : DEFAULT_Y_AXIS_STEP;

  return buildStandardBridgeYAxisLayout({
    referenceLineValue: 0,
    step,
    maxColumnTop: maxPositive,
    usesBridgeFocusedAxis: false,
    dataMin: minNegative < -step ? minNegative : undefined,
  });
};

export const buildBridgeFocusedYAxis = (chartRows: Array<BridgeChartRow>) => {
  const middleRows = chartRows.filter((row) => !row.isTotal);
  const totalRows = chartRows.filter((row) => row.isTotal);
  const { bridgeMin, maxMiddleDelta } = getMiddleBridgeExtents(middleRows);
  const totalBarTops = totalRows.map((row) => round(row.base + row.value, 2));
  const maxTotalBarTop =
    totalBarTops.length > 0 ? Math.max(...totalBarTops) : 0;
  const stepBasis = Math.max(maxMiddleDelta, maxTotalBarTop - bridgeMin, 0);

  const step =
    stepBasis > 0
      ? getNiceStepCeil(stepBasis / POSITIVE_Y_AXIS_STEP_COUNT)
      : DEFAULT_Y_AXIS_STEP;

  const axisBaseline = round(Math.max(0, bridgeMin - step), 2);

  return buildStandardBridgeYAxisLayout({
    referenceLineValue: axisBaseline,
    step,
    maxColumnTop: getMaxColumnTop(chartRows),
    usesBridgeFocusedAxis: true,
    dataMin: bridgeMin < axisBaseline - step ? bridgeMin : undefined,
  });
};
